'use client';

import dayjs from 'dayjs';
import React, { useState, useRef, useEffect } from 'react';

import { cn } from '@app/libs/utils';

import ArrowDownIcon from '../icons/ArrowDownIcon';
import CalendarIcon from '../icons/CalendarIcon';
import PlusIcon from '../icons/PlusIcon';

import { Button } from './Button';

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface DatePickerProps {
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  placeholder?: string;
  triggerClassName?: string;
  className?: string;
  disabled?: boolean;
  isRange?: boolean;
  applyWhenSelect?: boolean;
  dropdownClassName?: string;
  minDate?: Date;
  maxDate?: Date;
  format?: string;
  presets?: Array<{
    label: string;
    value: DateRange;
  }>;
  errors?: string[];
  type?: 'day' | 'month' | 'year';
  label?: string;
  description?: string;
  required?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value = { startDate: null, endDate: null },
  onChange,
  placeholder = 'Chọn thời gian',
  triggerClassName = '',
  className = '',
  disabled = false,
  isRange = false,
  applyWhenSelect = true,
  dropdownClassName = '',
  minDate,
  maxDate,
  format = 'DD/MM/YYYY',
  type = 'day',
  label,
  description,
  required = false,
  presets = [
    {
      label: 'Hôm nay',
      value: {
        startDate: dayjs().toDate(),
        endDate: new Date(),
      },
    },
    {
      label: 'Hôm qua',
      value: {
        startDate: dayjs().subtract(1, 'day').toDate(),
        endDate: dayjs().subtract(1, 'day').toDate(),
      },
    },
    {
      label: '7 ngày qua',
      value: {
        startDate: dayjs().subtract(7, 'day').toDate(),
        endDate: dayjs().toDate(),
      },
    },
    {
      label: '30 ngày qua',
      value: {
        startDate: dayjs().subtract(30, 'day').toDate(),
        endDate: dayjs().toDate(),
      },
    },
    {
      label: 'Tháng này',
      value: {
        startDate: dayjs().startOf('month').toDate(),
        endDate: dayjs().endOf('month').toDate(),
      },
    },
    {
      label: 'Tháng trước',
      value: {
        startDate: dayjs().subtract(1, 'month').startOf('month').toDate(),
        endDate: dayjs().subtract(1, 'month').endOf('month').toDate(),
      },
    },
  ],
  errors = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState<DateRange>(value);
  const [currentMonth, setCurrentMonth] = useState(dayjs().toDate());
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(type === 'month' ? true : false);
  const [isYearPickerOpen, setIsYearPickerOpen] = useState(type === 'year' ? true : false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [dropdownHorizontalPosition, setDropdownHorizontalPosition] = useState<'left' | 'right'>(
    'left'
  );
  const containerRef = useRef<HTMLDivElement>(null);
  // Helpers for min/max constraints
  const clampDate = (date: Date) => {
    let d = dayjs(date);
    if (minDate && d.isBefore(dayjs(minDate), 'day')) d = dayjs(minDate);
    if (maxDate && d.isAfter(dayjs(maxDate), 'day')) d = dayjs(maxDate);

    return d.toDate();
  };
  const isDateDisabled = (date: Date): boolean => {
    if (minDate && dayjs(date).isBefore(dayjs(minDate), 'day')) return true;
    if (maxDate && dayjs(date).isAfter(dayjs(maxDate), 'day')) return true;
    return false;
  };
  const isMonthDisabled = (y: number, m: number): boolean => {
    if (minDate) {
      if (y < dayjs(minDate).year()) return true;
      if (y === dayjs(minDate).year() && m < dayjs(minDate).month()) return true;
    }

    if (maxDate) {
      if (y > dayjs(maxDate).year()) return true;
      if (y === dayjs(maxDate).year() && m > dayjs(maxDate).month()) return true;
    }
    return false;
  };
  const isYearDisabled = (y: number): boolean => {
    if (minDate && y < dayjs(minDate).year()) return true;
    if (maxDate && y > dayjs(maxDate).year()) return true;
    return false;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as any)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update temp range when value changes
  useEffect(() => {
    const newTempRange = {
      startDate:
        value.startDate === null ||
        value.startDate === undefined ||
        value.startDate.toString() === 'Invalid Date'
          ? null
          : clampDate(value.startDate),
      endDate:
        value.endDate === null ||
        value.endDate === undefined ||
        value.endDate.toString() === 'Invalid Date'
          ? null
          : clampDate(value.endDate),
    };

    setTempRange(newTempRange);
  }, [value]);

  // Calculate dropdown position
  const calculateDropdownPosition = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 506; // Approximate height of dropdown
    const dropdownWidth = 320; // Approximate width of dropdown (min-w-[320px])

    // Check vertical position
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }

    // Check horizontal position
    const spaceRight = viewportWidth - rect.right;
    const spaceLeft = rect.left;

    if (spaceRight < dropdownWidth && spaceLeft > spaceRight) {
      // If there's more space on the left, align to the right
      setDropdownHorizontalPosition('right');
    } else {
      // Default: align to the left
      setDropdownHorizontalPosition('left');
    }
  };

  // Handle scroll and resize events
  useEffect(() => {
    if (!isOpen) return;

    const handleScrollAndResize = () => {
      calculateDropdownPosition();
    };

    window.addEventListener('scroll', handleScrollAndResize, true);
    window.addEventListener('resize', handleScrollAndResize);

    return () => {
      window.removeEventListener('scroll', handleScrollAndResize, true);
      window.removeEventListener('resize', handleScrollAndResize);
    };
  }, [isOpen]);

  const formatDate = (date: Date | null, fmt: string = 'DD/MM/YYYY'): string => {
    if (!date) return '';

    return dayjs(date).format(fmt);
  };

  const getDisplayText = (): string => {
    if (!tempRange.startDate && !tempRange.endDate) return placeholder;
    if (tempRange.startDate && tempRange.endDate) {
      return `${formatDate(tempRange.startDate, format)} - ${formatDate(tempRange.endDate, format)}`;
    }
    if (isRange) {
      if (tempRange.startDate) return `Từ ${formatDate(tempRange.startDate, format)}`;
      if (tempRange.endDate) return `Đến ${formatDate(tempRange.endDate, format)}`;
    } else {
      if (tempRange.startDate) return `${formatDate(tempRange.startDate, format)}`;
    }
    return placeholder;
  };

  const handlePresetClick = (preset: DateRange) => {
    let start = preset.startDate ? clampDate(preset.startDate) : null;
    let end = preset.endDate ? clampDate(preset.endDate) : null;
    if (start && end && end < start) {
      const tmp = start;
      start = end;
      end = tmp;
    }
    const next = { startDate: start, endDate: end };
    setTempRange(next);
    onChange?.(next);
    setIsOpen(false);
  };

  const handleToggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        calculateDropdownPosition();
      }
      setIsOpen(!isOpen);
    }
  };

  const handleDateClick = (date: Date, isStart: boolean = true) => {
    if (isDateDisabled(date)) return;
    if (isRange) {
      if (!tempRange.startDate || (tempRange.startDate && tempRange.endDate)) {
        // Start new range
        setTempRange({ startDate: date, endDate: null });
        if (applyWhenSelect) {
          onChange?.({
            startDate: isStart ? date : tempRange.startDate,
            endDate: isStart ? null : date,
          });
        }
      } else if (tempRange.startDate && !tempRange.endDate) {
        // Complete range
        let startDate = tempRange.startDate;
        let endDate = date;

        if (date < startDate) {
          startDate = date;
          endDate = tempRange.startDate;
        }

        switch (type) {
          case 'year':
            setTempRange({
              startDate: dayjs(startDate).startOf('year').toDate(),
              endDate: dayjs(endDate).endOf('year').toDate(),
            });
            break;
          case 'month':
            setTempRange({
              startDate: dayjs(startDate).startOf('month').toDate(),
              endDate: dayjs(endDate).endOf('month').toDate(),
            });
            break;
          default:
            setTempRange({ startDate, endDate });
            break;
        }

        if (applyWhenSelect) {
          onChange?.({ startDate, endDate });
          setIsOpen(false);
        }
      }
    } else {
      setTempRange({ startDate: date, endDate: null });
      if (applyWhenSelect) {
        onChange?.({ startDate: date, endDate: null });
        setIsOpen(false);
      }
    }
  };

  const handleApply = () => {
    onChange?.(tempRange);
    setIsOpen(false);
  };

  const handleClear = () => {
    const clearedRange = { startDate: null, endDate: null };
    setTempRange(clearedRange);
    onChange?.(clearedRange);
    setIsOpen(false);
  };

  const isDateInRange = (date: Date): boolean => {
    if (!tempRange.startDate) return false;
    if (!tempRange.endDate) return date.getTime() === tempRange.startDate.getTime();
    return date >= tempRange.startDate && date <= tempRange.endDate;
  };

  const isDateStart = (date: Date): boolean => {
    return tempRange.startDate?.getTime() === date.getTime();
  };

  const isDateEnd = (date: Date): boolean => {
    return tempRange.endDate?.getTime() === date.getTime();
  };

  const generateCalendarDays = (): Date[] => {
    const year = dayjs(currentMonth).year();
    const month = dayjs(currentMonth).month();
    const firstDay = dayjs(currentMonth).startOf('month').toDate();
    const lastDay = dayjs(currentMonth).endOf('month').toDate();
    const startDate = dayjs(firstDay).subtract(firstDay.getDay(), 'day').toDate();

    const days: Date[] = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (isYearPickerOpen) {
        // Trong chế độ chọn năm: prev/next điều chỉnh theo thập kỷ
        const delta = direction === 'prev' ? -12 : 12;
        newMonth.setFullYear(newMonth.getFullYear() + delta);
      } else if (isMonthPickerOpen) {
        // Trong chế độ chọn tháng: prev/next điều chỉnh năm
        if (direction === 'prev') {
          newMonth.setFullYear(newMonth.getFullYear() - 1);
        } else {
          newMonth.setFullYear(newMonth.getFullYear() + 1);
        }
      } else {
        // Chế độ lịch ngày: điều chỉnh theo tháng
        if (direction === 'prev') {
          newMonth.setMonth(newMonth.getMonth() - 1);
        } else {
          newMonth.setMonth(newMonth.getMonth() + 1);
        }
      }
      return newMonth;
    });
  };

  const days = generateCalendarDays();
  const weekDays = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div ref={containerRef} className="relative">
        {/* Input */}
        <div
          className={cn(
            'text-left text-sm border border-gray-300 rounded-md',
            'focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'hover:border-gray-400 transition-colors',
            'cursor-pointer flex items-center justify-between',
            errors.length > 0 && 'ring-2 ring-red-300',
            triggerClassName,
            !triggerClassName.match(/(^ )w-/) && 'w-full',
            !triggerClassName.match(/(^ )h-/) && 'h-10',
            disabled && 'bg-gray-100 cursor-not-allowed opacity-50',
            isOpen && 'border-blue-500'
          )}
        >
          <button
            type="button"
            className="px-3 py-2 w-full cursor-pointer text-left"
            onClick={handleToggleDropdown}
            disabled={disabled}
          >
            <span
              className={cn(
                'flex items-center gap-2 justify-between text-ellipsis overflow-hidden line-clamp-1',
                !value.startDate && !value.endDate && 'text-gray-500'
              )}
            >
              {getDisplayText()}
            </span>
          </button>
          <button
            type="button"
            className="px-3 py-2 cursor-pointer"
            onClick={() => (tempRange.startDate ? handleClear() : handleToggleDropdown())}
          >
            {tempRange.startDate ? (
              <PlusIcon className="w-4 h-4 rotate-45" />
            ) : (
              <CalendarIcon className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div
            className={cn(
              'absolute w-full min-w-[320px] bg-white border border-gray-200 rounded-lg shadow-lg z-50',
              dropdownClassName,
              !dropdownClassName.match(/(^ )w-/) && 'w-[320px]',
              dropdownHorizontalPosition === 'left' ? 'left-0' : 'right-0',
              dropdownPosition === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1'
            )}
          >
            <div className="p-4">
              {/* Presets - only show for day type */}
              {type === 'day' && presets.length > 0 && (
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">Khoảng thời gian nhanh</h4>
                  <div className="grid grid-cols-2 gap-1">
                    {presets.map((preset, index) => (
                      <button
                        key={index}
                        onClick={() => handlePresetClick(preset.value)}
                        className="px-2 py-1 text-xs text-left hover:bg-gray-100 rounded transition-colors cursor-pointer"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Calendar */}
              <div className="">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-3">
                  <Button
                    onClick={() => navigateMonth('prev')}
                    className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer p-0 h-6 w-6"
                    tooltip={dayjs(currentMonth)
                      .subtract(1, 'month')
                      .format(type === 'year' ? 'YYYY' : '[Tháng] MM, [năm] YYYY')}
                  >
                    <ArrowDownIcon className="w-4 h-4 rotate-90" />
                  </Button>

                  <div className="text-sm font-medium select-none flex items-center">
                    {type !== 'year' && (
                      <>
                        {/* Month label */}
                        <Button
                          type="button"
                          className="hover:text-blue-600 cursor-pointer p-0"
                          onClick={() => {
                            setIsYearPickerOpen(false);
                            setIsMonthPickerOpen(prev => (type === 'month' ? true : !prev));
                          }}
                          tooltip="Chọn tháng"
                        >
                          {currentMonth.toLocaleDateString('vi-VN', { month: 'long' })}
                        </Button>
                        <span className="mr-2">, </span>
                      </>
                    )}
                    {/* Year label */}
                    <Button
                      type="button"
                      className="hover:text-blue-600 cursor-pointer p-0"
                      onClick={() => {
                        setIsMonthPickerOpen(type === 'month');
                        setIsYearPickerOpen(prev => (type === 'year' ? true : !prev));
                      }}
                      tooltip="Chọn năm"
                    >
                      {currentMonth.toLocaleDateString('vi-VN', { year: 'numeric' })}
                    </Button>
                  </div>

                  <Button
                    onClick={() => navigateMonth('next')}
                    className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer p-0 h-6 w-6"
                    tooltip={dayjs(currentMonth).add(1, 'month').format('[Tháng] MM, [năm] YYYY')}
                  >
                    <ArrowDownIcon className="w-4 h-4 rotate-270" />
                  </Button>
                </div>
                {isYearPickerOpen ? (
                  // Year picker grid (12 years)
                  <div className="grid grid-cols-3 gap-2">
                    {(() => {
                      const startYear = Math.floor(currentMonth.getFullYear() / 12) * 12;
                      return Array.from({ length: 12 }).map((_, idx) => {
                        const y = startYear + idx;
                        const isCurrent = y === currentMonth.getFullYear();
                        const disabledYear = isYearDisabled(y);
                        return (
                          <Button
                            key={y}
                            onClick={() => {
                              if (disabledYear) return;
                              if (type === 'year') {
                                handleDateClick(dayjs().set('year', y).startOf('year').toDate());
                              }

                              setCurrentMonth(prev => {
                                const n = dayjs(prev).set('year', y).toDate();
                                return n;
                              });

                              setIsYearPickerOpen(type === 'year');
                            }}
                            className={cn(
                              'px-3 py-2 text-xs rounded border transition-colors w-full',
                              disabledYear
                                ? 'text-gray-300 cursor-not-allowed opacity-60 border-gray-200'
                                : 'cursor-pointer',
                              !disabledYear &&
                                (isCurrent
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'hover:bg-gray-100 border-gray-200')
                            )}
                            disabled={disabledYear}
                            tooltip={y === dayjs().year() ? 'Năm hiện tại' : ''}
                          >
                            {y}
                          </Button>
                        );
                      });
                    })()}
                  </div>
                ) : isMonthPickerOpen ? (
                  // Month picker grid
                  <div className="grid grid-cols-3 gap-2">
                    {Array.from({ length: 12 }).map((_, i) => {
                      const date = dayjs(currentMonth).set('month', i).startOf('month').toDate();
                      const isCurrent = i === currentMonth.getMonth();
                      const disabledMonth = isMonthDisabled(currentMonth.getFullYear(), i);
                      const labelMonth = date.toLocaleDateString('vi-VN', { month: 'short' });
                      return (
                        <Button
                          key={i}
                          onClick={() => {
                            if (disabledMonth) return;
                            if (type === 'month') {
                              handleDateClick(
                                dayjs()
                                  .set('month', i)
                                  .set('year', currentMonth.getFullYear())
                                  .startOf('month')
                                  .toDate()
                              );
                              setCurrentMonth(
                                dayjs()
                                  .set('month', i)
                                  .set('year', currentMonth.getFullYear())
                                  .startOf('month')
                                  .toDate()
                              );
                            }

                            setCurrentMonth(prev => {
                              const n = new Date(prev);
                              n.setMonth(i);
                              return n;
                            });

                            setIsMonthPickerOpen(type === 'month');
                          }}
                          className={cn(
                            'px-3 py-2 text-xs rounded border transition-colors w-full',
                            disabledMonth
                              ? 'text-gray-300 cursor-not-allowed opacity-60 border-gray-200'
                              : 'cursor-pointer',
                            !disabledMonth &&
                              (isCurrent
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'hover:bg-gray-100 border-gray-200')
                          )}
                          disabled={disabledMonth}
                          tooltip={
                            dayjs(date).format('MM-YYYY') === dayjs().format('MM-YYYY')
                              ? 'Tháng hiện tại'
                              : ''
                          }
                        >
                          {labelMonth}
                        </Button>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    {/* Week Days */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {weekDays.map((day, index) => (
                        <div key={index} className="text-xs text-center text-gray-500 py-1">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1">
                      {days.map((date, index) => {
                        const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                        const isToday = date.toDateString() === dayjs().toDate().toDateString();
                        const isInRange = isDateInRange(date);
                        const isStart = isDateStart(date);
                        const isEnd = isDateEnd(date);
                        const disabledDay = isDateDisabled(date);

                        return (
                          <React.Fragment key={index}>
                            {isToday ? (
                              <Button
                                tooltip="Hôm nay"
                                key={index}
                                onClick={() => handleDateClick(date)}
                                className={cn(
                                  'w-8 h-8 text-xs rounded transition-colors',
                                  !isCurrentMonth && 'text-gray-300',
                                  isCurrentMonth &&
                                    !disabledDay &&
                                    'text-gray-700 hover:bg-gray-100 cursor-pointer',
                                  disabledDay && 'text-gray-100 cursor-not-allowed opacity-60',
                                  isToday && 'bg-blue-100 text-blue-700 font-medium',
                                  isInRange && 'bg-blue-50',
                                  isStart && 'bg-blue-500 text-white hover:bg-blue-600',
                                  isEnd && 'bg-blue-500 text-white hover:bg-blue-600',
                                  isStart && isEnd && 'rounded-full'
                                )}
                                disabled={disabledDay}
                              >
                                {date.getDate()}
                              </Button>
                            ) : (
                              <Button
                                key={index}
                                onClick={() => handleDateClick(date)}
                                className={cn(
                                  'w-8 h-8 text-xs rounded transition-colors',
                                  !isCurrentMonth && 'text-gray-300',
                                  isCurrentMonth &&
                                    !disabledDay &&
                                    'text-gray-700 hover:bg-gray-100 cursor-pointer',
                                  disabledDay && 'text-gray-100 cursor-not-allowed opacity-60',
                                  isToday && 'bg-blue-100 text-blue-700 font-medium',
                                  isInRange && 'bg-blue-50',
                                  isStart && 'bg-blue-500 text-white hover:bg-blue-600',
                                  isEnd && 'bg-blue-500 text-white hover:bg-blue-600',
                                  isStart && isEnd && 'rounded-full'
                                )}
                                disabled={disabledDay}
                                tooltip={!isCurrentMonth ? dayjs(date).format('[Tháng] MM') : ''}
                              >
                                {date.getDate()}
                              </Button>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center mt-4 pt-2 border-t">
                <div className="flex gap-2">
                  <Button
                    onClick={() => setIsOpen(false)}
                    variant="secondary"
                    outline
                    size="xs"
                    className="h-8"
                  >
                    Hủy
                  </Button>
                  {!applyWhenSelect && (
                    <Button onClick={handleApply} variant="primary" size="xs" className="h-8">
                      Áp dụng
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {description && <span className="text-xs text-green-500 -mt-1">{description}</span>}

      {errors.length > 0 && (
        <div className="flex flex-col gap-1">
          {errors.map((error, index) => (
            <span key={index} className="text-xs text-red-500">
              {error}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
